/**
 * Explicit correlation matrices (PRD §4.3, the advanced model).
 *
 * Correlation groups cover the common cases — one reel, a matched pair — with a
 * single number and an O(1) sampling path. They cannot express an asymmetric
 * structure: A strongly tied to B, B weakly tied to C, A and C independent.
 * That needs a matrix, sampled through a Gaussian copula whose Cholesky factor
 * is computed once at model build.
 *
 * Groups are kept as their own fast path rather than being folded into a
 * 1-by-1 matrix. Two reasons: the group path costs O(1) per variable against
 * the matrix's O(k) row product, and rewriting groups in terms of Cholesky
 * would change which samples a given seed produces, silently invalidating every
 * saved analysis for no gain in expressiveness.
 *
 * A correlation matrix that is not positive semi-definite describes no joint
 * distribution at all. That is easy to write down by accident — three variables
 * each 0.9 correlated with the others is fine, but 0.9, 0.9 and -0.9 is not —
 * so the matrix is checked, and a nearest valid matrix can be offered.
 */

export interface CorrelationPair {
  a: string;
  b: string;
  rho: number;
}

export class CorrelationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CorrelationError';
  }
}

export interface CorrelationStructure {
  /** Variable ids participating, in matrix order. */
  ids: string[];
  /** Row-major k x k correlation matrix. */
  matrix: Float64Array;
  /** Lower-triangular Cholesky factor, row-major. */
  cholesky: Float64Array;
  /** True when the supplied matrix had to be repaired to become valid. */
  repaired: boolean;
  /** Largest absolute change made by the repair. */
  repairMagnitude: number;
}

/** Assemble a full correlation matrix from sparse pairwise entries. */
export function buildMatrix(pairs: CorrelationPair[]): { ids: string[]; matrix: Float64Array } {
  const ids: string[] = [];
  const index = new Map<string, number>();
  const idOf = (name: string) => {
    let i = index.get(name);
    if (i === undefined) {
      i = ids.length;
      index.set(name, i);
      ids.push(name);
    }
    return i;
  };

  for (const p of pairs) {
    if (p.a === p.b) {
      throw new CorrelationError(`Cannot correlate '${p.a}' with itself`);
    }
    if (!(p.rho >= -1 && p.rho <= 1)) {
      throw new CorrelationError(
        `Correlation between '${p.a}' and '${p.b}' must lie in [-1, 1], got ${p.rho}`,
      );
    }
    idOf(p.a);
    idOf(p.b);
  }

  const k = ids.length;
  const matrix = new Float64Array(k * k);
  for (let i = 0; i < k; i++) matrix[i * k + i] = 1;

  for (const p of pairs) {
    const i = index.get(p.a)!;
    const j = index.get(p.b)!;
    matrix[i * k + j] = p.rho;
    matrix[j * k + i] = p.rho;
  }

  return { ids, matrix };
}

/**
 * Cholesky factorisation. Returns null when the matrix is not positive definite.
 *
 * A small ridge is tolerated on the diagonal so that a matrix which is merely
 * singular (perfect correlation, rho = 1) still factorises — that is a
 * legitimate model, not an error.
 */
export function cholesky(matrix: Float64Array, k: number): Float64Array | null {
  const l = new Float64Array(k * k);

  for (let i = 0; i < k; i++) {
    for (let j = 0; j <= i; j++) {
      let sum = matrix[i * k + j];
      for (let m = 0; m < j; m++) sum -= l[i * k + m] * l[j * k + m];

      if (i === j) {
        if (sum < -1e-9) return null; // genuinely indefinite
        // Exactly-singular is fine: a zero pivot means that variable is fully
        // determined by the earlier ones, which is what rho = 1 describes.
        l[i * k + j] = Math.sqrt(Math.max(0, sum));
      } else {
        const pivot = l[j * k + j];
        l[i * k + j] = pivot > 1e-300 ? sum / pivot : 0;
      }
    }
  }
  return l;
}

/**
 * Symmetric eigendecomposition by the cyclic Jacobi method.
 *
 * Correlation matrices here are small (a handful of correlated variables), so
 * the simplicity and unconditional stability of Jacobi is worth more than the
 * speed of a tridiagonal-QR approach.
 *
 * Returns eigenvalues and eigenvectors as columns of `vectors`.
 */
export function jacobiEigen(
  input: Float64Array,
  k: number,
  maxSweeps = 100,
): { values: Float64Array; vectors: Float64Array } {
  const a = Float64Array.from(input);
  const v = new Float64Array(k * k);
  for (let i = 0; i < k; i++) v[i * k + i] = 1;

  for (let sweep = 0; sweep < maxSweeps; sweep++) {
    let off = 0;
    for (let i = 0; i < k; i++) {
      for (let j = i + 1; j < k; j++) off += a[i * k + j] * a[i * k + j];
    }
    if (off < 1e-30) break;

    for (let p = 0; p < k - 1; p++) {
      for (let q = p + 1; q < k; q++) {
        const apq = a[p * k + q];
        if (Math.abs(apq) < 1e-300) continue;

        const theta = (a[q * k + q] - a[p * k + p]) / (2 * apq);
        const t =
          Math.sign(theta || 1) / (Math.abs(theta) + Math.sqrt(theta * theta + 1));
        const c = 1 / Math.sqrt(t * t + 1);
        const s = t * c;

        for (let i = 0; i < k; i++) {
          const aip = a[i * k + p];
          const aiq = a[i * k + q];
          a[i * k + p] = c * aip - s * aiq;
          a[i * k + q] = s * aip + c * aiq;
        }
        for (let i = 0; i < k; i++) {
          const api = a[p * k + i];
          const aqi = a[q * k + i];
          a[p * k + i] = c * api - s * aqi;
          a[q * k + i] = s * api + c * aqi;
        }
        for (let i = 0; i < k; i++) {
          const vip = v[i * k + p];
          const viq = v[i * k + q];
          v[i * k + p] = c * vip - s * viq;
          v[i * k + q] = s * vip + c * viq;
        }
      }
    }
  }

  const values = new Float64Array(k);
  for (let i = 0; i < k; i++) values[i] = a[i * k + i];
  return { values, vectors: v };
}

/**
 * Nearest valid correlation matrix, by eigenvalue clipping.
 *
 * Negative eigenvalues are raised to a small positive floor and the matrix is
 * rebuilt and renormalised so the diagonal returns to 1. This is the standard
 * first-resort repair; it is not the exact Frobenius-nearest matrix that
 * Higham's alternating-projections method finds, but it is close, it always
 * terminates, and — most importantly — the caller is TOLD a repair happened and
 * by how much, so an accidental typo does not quietly become a valid-looking
 * model.
 */
export function nearestValidCorrelation(
  matrix: Float64Array,
  k: number,
): { matrix: Float64Array; magnitude: number } {
  const { values, vectors } = jacobiEigen(matrix, k);

  const floor = 1e-8;
  const clipped = Float64Array.from(values, (x) => (x < floor ? floor : x));

  // Rebuild as V * diag(clipped) * V^T
  const rebuilt = new Float64Array(k * k);
  for (let i = 0; i < k; i++) {
    for (let j = 0; j < k; j++) {
      let sum = 0;
      for (let m = 0; m < k; m++) {
        sum += vectors[i * k + m] * clipped[m] * vectors[j * k + m];
      }
      rebuilt[i * k + j] = sum;
    }
  }

  // Renormalise to unit diagonal — the clipping perturbs it.
  const scale = new Float64Array(k);
  for (let i = 0; i < k; i++) scale[i] = Math.sqrt(Math.max(rebuilt[i * k + i], 1e-300));

  let magnitude = 0;
  for (let i = 0; i < k; i++) {
    for (let j = 0; j < k; j++) {
      const value = i === j ? 1 : rebuilt[i * k + j] / (scale[i] * scale[j]);
      rebuilt[i * k + j] = value;
      magnitude = Math.max(magnitude, Math.abs(value - matrix[i * k + j]));
    }
  }

  return { matrix: rebuilt, magnitude };
}

/**
 * Prepare a correlation structure for sampling.
 *
 * `repair` decides what happens to an invalid matrix: throw (so the user fixes
 * their model) or silently correct it. The default is to throw, because a
 * correlation matrix a user wrote down and a correlation matrix the tool
 * invented are different things, and only one of them should appear in a report
 * without comment.
 */
export function prepareCorrelation(
  pairs: CorrelationPair[],
  repair = false,
): CorrelationStructure | null {
  if (pairs.length === 0) return null;

  const { ids, matrix } = buildMatrix(pairs);
  const k = ids.length;

  let working = matrix;
  let repaired = false;
  let repairMagnitude = 0;

  let factor = cholesky(working, k);
  if (!factor) {
    if (!repair) {
      throw new CorrelationError(
        'The correlation matrix is not positive semi-definite, so it does not describe any ' +
          'joint distribution. This usually means one pair is inconsistent with the others — ' +
          'for example A and B strongly positive, B and C strongly positive, but A and C ' +
          'strongly negative. Fix the entries, or enable automatic repair to use the nearest ' +
          'valid matrix.',
      );
    }
    const fixed = nearestValidCorrelation(working, k);
    working = fixed.matrix;
    repaired = true;
    repairMagnitude = fixed.magnitude;
    factor = cholesky(working, k);
    if (!factor) {
      throw new CorrelationError('Could not repair the correlation matrix into a valid one');
    }
  }

  return { ids, matrix: working, cholesky: factor, repaired, repairMagnitude };
}
